import { PenLineIcon, SparklesIcon, UploadIcon } from "lucide-react"
import { useState } from "react"
import { cn } from "@/lib/cn"
import { isCsvFile } from "@/lib/is-csv-file"
import { AddTransactionDialog } from "@/routes/transactions/-add-transaction-dialog"
import { CsvImportDialog } from "@/routes/transactions/-csv-import-dialog"
import { UploadStatementDialog } from "@/routes/transactions/-upload-statement-dialog"
import { useSetSharedFile } from "@/store/selectors"
import { ConnectClaudeDialog } from "./connect-claude-dialog"

interface ChoiceRowProps {
  icon: React.ReactNode
  title: string
  description: string
  onClick: () => void
  dragProps?: React.HTMLAttributes<HTMLButtonElement>
  dragOver?: boolean
}

function ChoiceRow({ icon, title, description, onClick, dragProps, dragOver }: ChoiceRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...dragProps}
      // GlobalDropZone's opt-out attribute, same effect as a dialog/drawer's
      // own content — an in-page drop zone like this one must never also let
      // the window-level listener double-handle the same drop (PAISA-ING H1).
      data-no-global-drop={dragProps ? "" : undefined}
      className={cn(
        "flex w-full min-h-11 items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition-colors",
        dragOver
          ? "border-accent-green bg-accent-green/10"
          : "border-border-subtle bg-bg-surface hover:border-accent-green/40 hover:bg-bg-elevated",
      )}
    >
      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-bg-elevated text-text-primary">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-body font-medium text-text-primary">{title}</p>
        <p className="text-caption text-text-muted">{description}</p>
      </div>
    </button>
  )
}

interface FirstImportChoicesProps {
  /** One line of context shown above the three choices — differs by call site. */
  heading?: string
}

/**
 * The shared "bring your first month in" surface — three equal ways to get
 * transactions into Paisa. Used by onboarding's final step and the dashboard
 * empty state (PAISA-ING-01). Statements (PDF/XLS for supported banks, or CSV)
 * are parsed in the browser by the statement dialog; a CSV dropped here keeps
 * its column-mapping dialog. Other formats can go through Connect Claude.
 */
export function FirstImportChoices({ heading }: FirstImportChoicesProps) {
  const setSharedFile = useSetSharedFile()
  const [csvOpen, setCsvOpen] = useState(false)
  const [statementOpen, setStatementOpen] = useState(false)
  const [statementFile, setStatementFile] = useState<File | null>(null)
  const [claudeOpen, setClaudeOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [claudeContext, setClaudeContext] = useState<string | undefined>(undefined)

  function openCsvWithFile(file: File) {
    setSharedFile(file)
    setCsvOpen(true)
  }

  function handleFile(file: File) {
    if (isCsvFile(file)) {
      openCsvWithFile(file)
      return
    }
    // PDF / XLS statements are read on the device by the statement dialog (UAT J3-01).
    setStatementFile(file)
    setStatementOpen(true)
  }

  function onDrop(e: React.DragEvent<HTMLButtonElement>) {
    e.preventDefault()
    // Stop this drop from also reaching GlobalDropZone's window-level
    // listener — without this a CSV dropped here got handled twice
    // (nondeterministic teleport to /transactions, or silent file loss).
    e.stopPropagation()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    e.target.value = ""
  }

  return (
    <div className="w-full max-w-md space-y-2.5">
      {heading && <p className="text-body text-text-muted">{heading}</p>}

      <div className="relative">
        <ChoiceRow
          icon={<UploadIcon className="size-4.5" />}
          title="Upload a statement"
          description="Bank or card statement (PDF, Excel or CSV), read on your device"
          onClick={() => {
            setStatementFile(null)
            setStatementOpen(true)
          }}
          dragOver={dragOver}
          dragProps={{
            onDrop,
            onDragOver: (e) => {
              e.preventDefault()
              setDragOver(true)
            },
            onDragLeave: () => setDragOver(false),
          }}
        />
        <input
          id="first-import-csv-input"
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={onFileChange}
        />
      </div>

      <ChoiceRow
        icon={<SparklesIcon className="size-4.5" />}
        title="Connect Claude"
        description="Other banks and formats: Claude imports them for you"
        onClick={() => {
          setClaudeContext(undefined)
          setClaudeOpen(true)
        }}
      />

      <ChoiceRow
        icon={<PenLineIcon className="size-4.5" />}
        title="Add one manually"
        description="Just one transaction to get started"
        onClick={() => setAddOpen(true)}
      />

      <CsvImportDialog open={csvOpen} onClose={() => setCsvOpen(false)} />
      <UploadStatementDialog
        open={statementOpen}
        initialFile={statementFile}
        onManualAdd={() => setAddOpen(true)}
        onClose={() => {
          setStatementOpen(false)
          setStatementFile(null)
        }}
      />
      <ConnectClaudeDialog
        open={claudeOpen}
        onClose={() => setClaudeOpen(false)}
        context={claudeContext}
      />
      <AddTransactionDialog open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  )
}
